<script>
import bem from '@/utils/bem';
import camelCase from '@/utils/camelCase';

const b = bem('content-block');

export default {
  props: {
    content: {
      type: Object,
      required: true,
    },
    framesCount: {
      type: Number,
      required: false,
      default: 0,
    },
    frameIds: {
      type: Array,
      required: false,
      default: () => ([]),
    },
    openModal: {
      type: Function,
      required: false,
      default: () => {},
    },
    changeFrameCount: {
      type: Function,
      required: false,
      default: () => {},
    },
    setFrameIndex: {
      type: Function,
      required: false,
      default: () => {},
    },
    changeItemField: {
      type: Function,
      required: false,
      default: () => {},
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    generateStyle(style) {
      return Object.entries(style)
        .reduce((acc, [key, value]) => ({ ...acc, [camelCase(key)]: value }), {});
    },
    getDividerStyle(style) {
      const { 'border-width': height, 'border-color': color, ...rest } = style;
      return { ...rest, height, 'background-color': color };
    },
    getText(text) {
      const newText = text
        .split('[[')
        .reduce((acc, item) => {
          const [substring = '', continueText = ''] = item.split(']]');
          const [link = '', linkText = ''] = substring.split('|');
          return `${acc}<a href=${link} target="_blank">${linkText}</a>${continueText}`;
        })
        .split('<<')
        .reduce((acc, item) => {
          const [substring = '', continueText = ''] = item.split('>>');
          const [linkText = '', link = ''] = substring.split('|');
          return `${acc}<a href=${link} target="_blank">${linkText}</a>${continueText}`;
        });
      return newText;
    },
  },
};

</script>

<template>
  <div
    v-if="content.id === 'textBlock'"
    :class="b('content-text')"
    :id="content.contentId"
    :style="generateStyle(content.style || {})"
    v-html="getText(content.text)"
  />
  <h2
    v-if="content.id === 'HeadingBlock'"
    :class="b('content-heading')"
    :id="content.contentId"
    :style="generateStyle(content.style || {})"
  >
    {{content.text}}
  </h2>
  <button
    v-if="content.id === 'ButtonBlock'"
    :class="b('content-button')"
    :id="content.contentId"
    :style="generateStyle(content.style || {})"
  >
    {{content.text}}
  </button>
  <div
    v-if="content.id === 'DividerBlock'"
    :class="b('content-divider-wrap')"
    :id="content.contentId"
  >
    <div
      :class="b('content-divider')"
      :style="generateStyle(getDividerStyle(content.style || {}))"
    />
  </div>
  <div
    v-if="content.id === 'SpacerBlock'"
    :class="b('content-divider-wrap')"
    :id="content.contentId"
    :style="generateStyle(content.style || {})"
  />
  <img
    v-if="content.id === 'ImageBlock' && content.imageUri"
    :class="b('image-block')"
    :src="content.imageUri"
    :style="generateStyle(content.style || {})"
    alt="email-img"
  />
  <a-button
    v-if="content.id === 'ImageBlock' && !content.imageUri"
    type="primary"
    html-type="button"
    :class="b('add-image-btn')"
    @click="openModal(content.contentId)"
  >
    Add image
  </a-button>
  <div
    v-if="content.id === 'FramesBlock'"
    :class="b('template-block')"
  >
    <div :class="b('template-block-input-wrap')">
      {{content.text}}
      <a-input
        type="number"
        :value="framesCount"
        @change="(e) => {changeFrameCount(e.target.value)}"
      />
    </div>
    <div :class="b('template-block-frames-wrap')">
      <template
        v-for="(item, index) in frameIds"
        :key="() => `frame-${(item || {}).label || `index-${index}`}`"
      >
        <a-button
          v-if="!item"
          type="primary"
          html-type="button"
          :class="b('email-add-frame-btn')"
          @click="() => {setFrameIndex(index)}"
        >
          Select frame
        </a-button>
        <div
          v-else
          :class="b('email-frame-name')"
        >
          {{item.label}}
        </div>
      </template>
    </div>
  </div>
  <div
    v-if="content.id === 'Concierge'"
    :class="b('template-bloc')"
    :id="content.contentId"
  >
    <div :class="b('block-title')">{{content.text}}</div>
    <div :class="b('block-info-name')">Message link</div>
    <div :class="b('block-info-toggle-wrap')">
      <div :class="b('block-info-toggle-text')">Contact</div>
      <a-switch
        :checked="content.isMessage"
        :class="b('block-info-toggle')"
        checked-children=""
        un-checked-children=""
        @click="changeItemField('isMessage', !content.isMessage, content)"
      />
      <div :class="b('block-info-toggle-text')">Message</div>
    </div>
  </div>
  <div
    v-if="content.id === 'CustomHeader'"
    :class="b('template-block')"
  >
    <div :class="b('block-title')">🎨 {{content.text}}</div>
    <div v-if="content.logoUrl" :class="b('block-info-name')">
      🖼️ Logo: {{ content.logoUrl.split('/').pop() }}
    </div>
    <div :class="b('block-info-toggle-wrap')">
      <span>📦 Track Order</span>
      <span :style="{
        color: content.showTrackOrder ? '#28a745' : '#dc3545',
        backgroundColor: content.showTrackOrder ? '#d4edda' : '#f8d7da'
      }">
        {{ content.showTrackOrder ? '✓ Enabled' : '✗ Disabled' }}
      </span>
    </div>
    <div :class="b('block-info-toggle-wrap')">
      <span>👤 My Account</span>
      <span :style="{
        color: content.showMyAccount ? '#28a745' : '#dc3545',
        backgroundColor: content.showMyAccount ? '#d4edda' : '#f8d7da'
      }">
        {{ content.showMyAccount ? '✓ Enabled' : '✗ Disabled' }}
      </span>
    </div>
    <div :class="b('block-info-toggle-wrap')">
      <span>🔗 Custom Link</span>
      <span :style="{
        color: content.showCustomLink ? '#28a745' : '#dc3545',
        backgroundColor: content.showCustomLink ? '#d4edda' : '#f8d7da'
      }">
        {{ content.showCustomLink ? '✓ Enabled' : '✗ Disabled' }}
      </span>
    </div>
    <div v-if="content.showCustomLink" :class="b('block-info-name')">
      ✏️ Link: {{ content.customLinkText || 'Custom Link' }}
    </div>
  </div>
  <div
    v-if="content.id === 'CustomFooter'"
    :class="b('template-block')"
  >
    <div :class="b('block-title')">📄 {{content.text}}</div>
    <div :class="b('block-info-toggle-wrap')">
      <span>📝 Top Block</span>
      <span :style="{
        color: content.showTopBlock ? '#28a745' : '#dc3545',
        backgroundColor: content.showTopBlock ? '#d4edda' : '#f8d7da'
      }">
        {{ content.showTopBlock ? '✓ Enabled' : '✗ Disabled' }}
      </span>
    </div>
    <div v-if="content.showTopBlock && content.showBlogButton" :class="b('block-info-name')">
      🔗 Blog Button: {{ content.blogButtonText || 'Blog' }}
    </div>
    <div :class="b('block-info-toggle-wrap')">
      <span>🌐 Social Links</span>
      <span :style="{
        color: content.showSocialLinks ? '#28a745' : '#dc3545',
        backgroundColor: content.showSocialLinks ? '#d4edda' : '#f8d7da'
      }">
        {{ content.showSocialLinks ? '✓ Enabled' : '✗ Disabled' }}
      </span>
    </div>
    <div v-if="content.showSocialLinks" :class="b('block-info-name')">
      {{[content.showFacebook && '📘 Facebook',
        content.showTwitter && '🐦 Twitter',
        content.showInstagram && '📷 Instagram',
        content.showYoutube && '📺 YouTube'
      ].filter(Boolean).join(', ') || 'No social links'}}
    </div>
    <div :class="b('block-info-toggle-wrap')">
      <span>🔗 Bottom Navigation</span>
      <span :style="{
        color: content.showBottomLinks ? '#28a745' : '#dc3545',
        backgroundColor: content.showBottomLinks ? '#d4edda' : '#f8d7da'
      }">
        {{ content.showBottomLinks ? '✓ Enabled' : '✗ Disabled' }}
      </span>
    </div>
    <div :class="b('block-info-toggle-wrap')">
      <span>📞 Contact Info</span>
      <span :style="{
        color: (content.showPhone || content.showAddress) ? '#28a745' : '#dc3545',
        backgroundColor: (content.showPhone || content.showAddress) ? '#d4edda' : '#f8d7da'
      }">
        {{ (content.showPhone || content.showAddress) ? '✓ Enabled' : '✗ Disabled' }}
      </span>
    </div>
  </div>
  <div
    v-if="[
      'ImagesBlock',
      'Header',
      'PreFooter',
      'Icons',
      'Footer',
      'InvoiceBlock'
    ].includes(content.id)"
    :class="b('template-block')"
  >
    {{content.text}}
  </div>
</template>;

<style src="./contentBlockstyle.scss" scoped lang="scss"></style>
